import React from 'react';
import {
  GitBranch, Shield, Lock, UserCheck, FileDiff, GitMerge,
  Eye, CheckCircle2, AlertTriangle, Users, Server, ClipboardList
} from 'lucide-react';

const LAST_UPDATED = 'May 23, 2026';
const POLICY_OWNER = 'Altaf Rehman — AI Engineer, BrainCX';

const TEAM = [
  {
    name: 'Altaf Rehman',
    title: 'AI Engineer',
    responsibilities:
      'Owns the production repository. Develops, reviews, integrates, and merges all production code. Sole authority to merge into the production branch.',
  },
  {
    name: 'Hiba Amjad',
    title: 'AI Developer',
    responsibilities:
      'Develops assigned features in an isolated local environment. Submits git diffs for review. Does not have access to the production repository or merge privileges.',
  },
  {
    name: 'Daniyal Raza',
    title: 'AI Developer',
    responsibilities:
      'Develops assigned features in an isolated local environment. Submits git diffs for review. Does not have access to the production repository or merge privileges.',
  },
];

const SECTIONS = [
  {
    id: 'overview',
    title: 'Overview',
    icon: ClipboardList,
    content: (
      <>
        <p>
          This document describes BrainCX&apos;s formal process for merging code into the production branch
          of our primary application repository. It is maintained to satisfy SOC 2 change-management
          controls and to ensure that all production deployments are authorized, reviewed, and traceable.
        </p>
        <p className="mt-3">
          The production codebase is owned entirely by BrainCX. All changes that reach production follow
          the workflows defined below — whether developed in-house or submitted by external contributors
          working on isolated development environments.
        </p>
      </>
    ),
  },
  {
    id: 'repository-access',
    title: 'Repository Ownership & Access Control',
    icon: Lock,
    content: (
      <>
        <p>
          The production repository is private and owned by the organization from inception. Access is
          restricted as follows:
        </p>
        <ul className="mt-3 space-y-2 list-none">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span>
              <strong className="text-foreground/90">Production repository:</strong> Accessible only to
              Altaf Rehman (AI Engineer) with merge privileges. No other personnel can view, clone,
              or download the full production codebase.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span>
              <strong className="text-foreground/90">AI Developers:</strong> Hiba Amjad and Daniyal Raza do
              not receive access to the production repository. They work against a minimal, stripped-down
              local development environment that contains only the components required to build and test
              assigned features.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span>
              <strong className="text-foreground/90">Credential isolation:</strong> Production secrets,
              infrastructure credentials, and customer data are never included in contributor development
              environments.
            </span>
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'production-branch',
    title: 'Production Branch Protection',
    icon: Shield,
    content: (
      <>
        <p>
          The production branch (main) is protected. Only Altaf Rehman (AI Engineer) can merge changes
          into production. No direct pushes to production are permitted from any other account or role.
        </p>
        <div className="mt-4 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-foreground/80">
              All production merges require explicit authorization from Altaf Rehman. There is no
              automated or delegated merge path to production for AI Developers or third-party tooling.
            </p>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'internal-development',
    title: 'Internal Development Workflow',
    icon: GitBranch,
    content: (
      <>
        <p>
          The majority of production code is developed directly by Altaf Rehman (AI Engineer) within
          the production repository. The standard workflow is:
        </p>
        <ol className="mt-4 space-y-3 list-none counter-reset-none">
          {[
            'Changes are developed and tested locally against a full development environment.',
            'Changes are committed to a feature or fix branch within the private repository.',
            'The author performs code review and functional testing before merge.',
            'Altaf Rehman merges the approved branch into the production branch.',
            'The production deployment is triggered from the updated production branch.',
          ].map((step, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex-shrink-0">
                {idx + 1}
              </span>
              <span className="pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
      </>
    ),
  },
  {
    id: 'external-contributors',
    title: 'AI Developer Workflow',
    icon: FileDiff,
    content: (
      <>
        <p>
          For specific modules developed by AI Developers (Hiba Amjad and Daniyal Raza), a controlled
          handoff process is used instead of direct repository access:
        </p>
        <ol className="mt-4 space-y-3 list-none">
          {[
            {
              step: 'Isolated environment provisioned',
              detail:
                'The AI Developer receives a minimal local development environment containing only the module and dependencies required for their assigned work. This environment does not include production infrastructure, secrets, or the full application codebase.',
            },
            {
              step: 'Feature development and local testing',
              detail:
                'The AI Developer develops and tests the feature within their isolated environment.',
            },
            {
              step: 'Git diff submission',
              detail:
                'Upon completion, the AI Developer submits a git diff (patch file) documenting all proposed changes to Altaf Rehman.',
            },
            {
              step: 'Review and integration',
              detail:
                'Altaf Rehman reviews the submitted diff for correctness, security, and compatibility with the production codebase.',
            },
            {
              step: 'Manual application to production repository',
              detail:
                'After approval, Altaf Rehman manually applies the reviewed changes into the private production repository.',
            },
            {
              step: 'Production merge',
              detail:
                'Only Altaf Rehman merges the integrated changes into the production branch.',
            },
          ].map((item, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex-shrink-0">
                {idx + 1}
              </span>
              <div>
                <p className="font-medium text-foreground/90">{item.step}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{item.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </>
    ),
  },
  {
    id: 'review-approval',
    title: 'Review & Approval Requirements',
    icon: Eye,
    content: (
      <>
        <p>Before any change is merged to production, the following review criteria are applied:</p>
        <ul className="mt-3 space-y-2">
          {[
            'Functional correctness — the change behaves as intended in a test environment.',
            'Security review — no credentials, secrets, or sensitive data are introduced.',
            'Compatibility — the change integrates cleanly with existing production code.',
            'Scope validation — the change is limited to the approved feature or fix.',
            'Authorization — merge is performed only by Altaf Rehman (AI Engineer).',
          ].map((item, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    id: 'roles',
    title: 'Roles & Responsibilities',
    icon: Users,
    content: (
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Name</th>
              <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Title</th>
              <th className="text-left py-2 font-medium text-muted-foreground">Responsibilities</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {TEAM.map(member => (
              <tr key={member.name}>
                <td className="py-3 pr-4 align-top font-medium text-foreground/90 whitespace-nowrap">
                  {member.name}
                </td>
                <td className="py-3 pr-4 align-top text-muted-foreground whitespace-nowrap">
                  {member.title}
                </td>
                <td className="py-3 text-muted-foreground">{member.responsibilities}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: 'deployment',
    title: 'Production Deployment',
    icon: Server,
    content: (
      <>
        <p>
          Production deployments are triggered exclusively from the production branch after an authorized
          merge. The deployment process follows these principles:
        </p>
        <ul className="mt-3 space-y-2">
          {[
            'Only code on the production branch is deployed to production infrastructure.',
            'Deployments are performed by Altaf Rehman (AI Engineer).',
            'Each production merge corresponds to a traceable set of commits.',
            'Release notes are published documenting changes included in each deployment.',
          ].map((item, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
];

const ProcessFlow = () => (
  <div className="rounded-xl border border-border bg-card p-5">
    <div className="flex items-center gap-2 mb-4">
      <GitMerge className="w-4 h-4 text-primary" />
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Production Merge Flow
      </span>
    </div>
    <div className="space-y-0">
      {[
        { label: 'Development', sub: 'Internal or isolated environment' },
        { label: 'Review & Test', sub: 'Functional + security validation' },
        { label: 'Integration', sub: 'Applied to private repository' },
        { label: 'Merge to Production', sub: 'Altaf Rehman (AI Engineer)' },
        { label: 'Deploy', sub: 'Production infrastructure updated' },
      ].map((step, idx, arr) => (
        <div key={idx} className="flex items-start gap-3">
          <div className="flex flex-col items-center">
            <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary">
              {idx + 1}
            </div>
            {idx < arr.length - 1 && (
              <div className="w-px h-6 bg-border my-1" />
            )}
          </div>
          <div className="pb-4">
            <p className="text-sm font-medium text-foreground/90">{step.label}</p>
            <p className="text-xs text-muted-foreground">{step.sub}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SectionCard = ({ section }) => {
  const Icon = section.icon;
  return (
    <section id={section.id} className="rounded-xl border border-border bg-card shadow-card overflow-hidden scroll-mt-28">
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-border bg-primary/5">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary">
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="text-base font-semibold">{section.title}</h2>
      </div>
      <div className="p-5 text-sm text-muted-foreground leading-relaxed">
        {section.content}
      </div>
    </section>
  );
};

const ProductionMergePolicyPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="fixed top-0 left-0 right-0 z-10 flex-shrink-0 border-b border-border bg-card/95 backdrop-blur-sm px-6 py-4">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary">
              <GitMerge className="w-4 h-4" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">Production Code Merge Policy</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                <UserCheck className="w-3 h-3" />
                {POLICY_OWNER} · Last updated {LAST_UPDATED}
              </p>
            </div>
          </div>
          
        </div>
      </header>

      <main className="flex-1 p-6 pt-28">
        <div className="max-w-[1200px] mx-auto flex gap-6 flex-col lg:flex-row lg:items-start">
          <aside className="flex-shrink-0 lg:w-[280px]">
            <div className="flex flex-col gap-4 lg:sticky lg:top-24">
              <ProcessFlow />
              <nav className="rounded-xl border border-border bg-card p-4 hidden lg:block">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
                  On this page
                </p>
                <ul className="space-y-1">
                  {SECTIONS.map(s => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className="block text-xs text-muted-foreground hover:text-primary transition-colors py-1"
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </aside>

          <div className="flex-1 min-w-0 flex flex-col gap-5">
            {SECTIONS.map(section => (
              <SectionCard key={section.id} section={section} />
            ))}

            <footer className="text-center text-xs text-muted-foreground py-4 border-t border-border mt-2">
              This policy is reviewed periodically and updated as engineering processes evolve.
              For questions, contact Altaf Rehman (AI Engineer).
            </footer>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProductionMergePolicyPage;
